import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HardHat, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtEuro } from "@/lib/format";
import { Btn, Chip, Empty, PageHeader, Pill, Spinner } from "@/components/rp";
import { Section } from "@/components/commesse";
import { Dot, Legend, SUB_LABELS } from "@/components/admin/labels";

const Access = () => {
  const [a, setA] = useState(null);
  const [ids, setIds] = useState([]);
  useEffect(() => { api.get("/amministrazione/settings/subappalti-access").then((r) => { setA(r.data); setIds(r.data.staff_ids); }); }, []);
  if (!a) return null;
  const save = async () => { try { const { data } = await api.put("/amministrazione/settings/subappalti-access", { staff_ids: ids }); setA(data); toast.success("Accessi aggiornati"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Section label="Chi vede i subappalti" testid="sub-access" action={<Btn className="h-9" onClick={save} disabled={!ids.length} data-testid="sub-access-save"><ShieldCheck className="h-4 w-4" /> Salva</Btn>}>
      {a.open_to_all && <p className="text-sm text-[#B26A00] mb-3" data-testid="sub-access-warning">Non ci sono ancora account di Serena o Roberto: finché non scegli qualcuno la sezione è visibile a tutto lo staff.</p>}
      <div className="flex flex-wrap gap-2">
        {a.staff.map((s) => <Chip key={s.id} active={ids.includes(s.id)} onClick={() => setIds(ids.includes(s.id) ? ids.filter((x) => x !== s.id) : [...ids, s.id])} data-testid={`sub-access-${s.id}`}>{s.name}</Chip>)}
      </div>
    </Section>
  );
};

const Matrix = ({ rows, columns }) => (
  <div className="rp-card overflow-x-auto max-w-full" data-testid="sub-matrix">
    <table className="w-full text-sm">
      <thead><tr className="text-left">
        <th className="p-3 rp-label sticky left-0 bg-white min-w-[220px]">Cantiere · ditta</th>
        {columns.map(([k]) => <th key={k} className="p-2 text-[10px] font-semibold text-[#757575] align-bottom min-w-[74px]"><span className="block [writing-mode:vertical-rl] rotate-180 max-h-44 mx-auto">{SUB_LABELS[k]}</span></th>)}
        <th className="p-3 rp-label text-right">Pagato</th><th className="p-3 rp-label text-right">Da pagare</th>
      </tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.party_id} className="border-t border-[#EBEBEB]" data-testid={`sub-row-${r.party_id}`}>
            <td className="p-3 sticky left-0 bg-white">
              <Link to={`/admin/commesse/${r.order_id}`} className="font-medium hover:underline">{r.name}</Link>
              <p className="text-xs text-[#757575]">{r.code} · {r.title}</p>
              <div className="flex gap-1 mt-1">{r.expired > 0 && <Pill tone="error">{r.expired} scaduti</Pill>}{r.overdue > 0 && <Pill tone="warning">{r.overdue} in ritardo</Pill>}</div>
            </td>
            {columns.map(([k]) => <td key={k} className="p-2 text-center"><Dot s={r.items[k]} testid={`sub-dot-${r.party_id}-${k}`} />{r.items[k]?.files > 0 && <span className="block text-[10px] text-[#757575]">{r.items[k].files} file</span>}</td>)}
            <td className="p-3 text-right whitespace-nowrap">{fmtEuro(r.paid)}</td><td className="p-3 text-right whitespace-nowrap font-semibold">{fmtEuro(r.to_pay)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function AdminSubappalti() {
  const [d, setD] = useState(null);
  const [denied, setDenied] = useState(false);
  useEffect(() => { api.get("/amministrazione/subappalti").then((r) => setD(r.data)).catch(() => setDenied(true)); }, []);
  if (denied) return <Empty icon={ShieldCheck} title="Sezione riservata" text="I subappalti sono visibili solo alle persone autorizzate." />;
  if (!d) return <Spinner />;
  const tot = d.rows.reduce((s, r) => ({ paid: s.paid + r.paid, to_pay: s.to_pay + r.to_pay, expired: s.expired + r.expired }), { paid: 0, to_pay: 0, expired: 0 });
  return (
    <div className="space-y-6" data-testid="admin-subappalti-page">
      <PageHeader label="Area riservata" title="Subappalti" />
      <div className="grid sm:grid-cols-4 gap-3 -mt-4">
        {[["Ditte attive", d.rows.length], ["Pagato", fmtEuro(tot.paid)], ["Da pagare", fmtEuro(tot.to_pay)], ["Documenti scaduti", tot.expired]].map(([l, v]) => (
          <div key={l} className="rp-card p-4" data-testid={`sub-kpi-${l.split(" ")[0].toLowerCase()}`}><p className="rp-label">{l}</p><p className="text-2xl font-semibold mt-1">{v}</p></div>
        ))}
      </div>
      <Legend />
      {d.rows.length ? <Matrix rows={d.rows} columns={d.columns} /> : <Empty icon={HardHat} title="Nessun subappalto" text="Le ditte si aggiungono nella scheda Subappalti di ogni cantiere." />}
      <Access />
    </div>
  );
}
